import React from 'react';

import { useNavigate } from 'react-router-dom';

import { motion } from 'framer-motion';
import { BarChart, ChevronRight, Globe, Star, Users } from 'lucide-react';

import { Service } from '../../types/serviceTypes';

interface StatItem {
  label: string;
  value: string;
  icon: React.ReactNode;
}

interface EnhancedServiceCardProps {
  service: Service;
  isFeatured?: boolean;
  isHighlighted?: boolean;
  showStats?: boolean;
  customStats?: StatItem[];
  customBenefits?: string[];
}

const EnhancedServiceCard: React.FC<EnhancedServiceCardProps> = ({
  service,
  isFeatured = false,
  isHighlighted = false,
  showStats = false,
  customStats,
}) => {
  // Obtener la función de navegación
  const navigate = useNavigate();

  // Función para manejar la navegación al servicio
  const handleNavigateToService = (e?: React.MouseEvent) => {
    // Prevenir el comportamiento predeterminado si hay un evento
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    navigate(`/servicios/${service.id}`);
    window.scrollTo(0, 0);
  };

  // Estadísticas específicas para Presencia Digital Profesional (por defecto)
  const defaultDigitalPresenceStats = [
    { label: 'Visibilidad', value: '+65%', icon: <Globe className="h-3 w-3 text-blue-500" /> },
    { label: 'Clientes', value: '+45%', icon: <Users className="h-3 w-3 text-green-500" /> },
    { label: 'Conversión', value: '+35%', icon: <BarChart className="h-3 w-3 text-yellow-500" /> },
  ];

  // Usar estadísticas personalizadas si están disponibles, o las predeterminadas si no
  const serviceStats = customStats || defaultDigitalPresenceStats;

  return (
    <motion.div
      whileHover={{
        y: -8,
        boxShadow: '0 20px 35px -10px rgba(0, 0, 0, 0.15), 0 15px 20px -10px rgba(0, 0, 0, 0.08)',
      }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className={`bg-white rounded-xl shadow-md overflow-hidden h-full flex flex-col border backdrop-blur-sm relative ${isHighlighted ? 'border-blue-300 shadow-blue-100' : 'border-gray-100'}`}
    >
      {/* Elementos decorativos de fondo */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 opacity-30 rounded-full -mt-10 -mr-10 z-0"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-50 opacity-20 rounded-full -mb-8 -ml-8 z-0"></div>

      {/* Efecto de partículas para la tarjeta destacada */}
      {isHighlighted && (
        <>
          <div
            className="absolute top-1/4 right-1/4 w-3 h-3 bg-blue-300 rounded-full opacity-70 animate-pulse"
            style={{ animationDuration: '4s' }}
          ></div>
          <div
            className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-yellow-200 rounded-full opacity-80 animate-pulse"
            style={{ animationDuration: '3.5s' }}
          ></div>
          <div
            className="absolute top-2/3 right-1/2 w-4 h-4 bg-blue-200 rounded-full opacity-60 animate-pulse"
            style={{ animationDuration: '5s' }}
          ></div>
        </>
      )}

      {isFeatured && (
        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 px-4 py-2 flex items-center justify-center shadow-md relative z-10 overflow-hidden">
          <div
            className="absolute inset-0 bg-white opacity-10 animate-pulse"
            style={{ animationDuration: '3s' }}
          ></div>
          <Star className="h-4 w-4 mr-2 text-blue-900 animate-pulse" />
          <span className="text-sm font-bold tracking-wide">Servicio Destacado</span>
          <div className="absolute -right-4 -top-4 w-12 h-12 bg-yellow-300 opacity-20 rounded-full"></div>
        </div>
      )}

      <div className="p-4 flex-grow relative z-10">
        <div className="flex items-center mb-5">
          <div
            className={`bg-gradient-to-br from-blue-50 to-blue-100 p-3.5 rounded-lg inline-flex items-center justify-center mr-3.5 shadow-md relative overflow-hidden group ${isHighlighted ? 'from-blue-100 to-blue-200' : ''}`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            {typeof service.icon === 'string' ? (
              <div className="h-7 w-7 text-blue-600 relative z-10">{service.icon}</div>
            ) : service.icon ? (
              typeof service.icon === 'function' ? (
                React.createElement(service.icon, {
                  className: 'h-7 w-7 text-blue-600 animate-float relative z-10',
                })
              ) : (
                <div className="h-7 w-7 text-blue-600 relative z-10">{service.icon}</div>
              )
            ) : (
              <div className="h-7 w-7 text-blue-600 relative z-10">🔍</div>
            )}
            <div className="absolute top-0 left-0 w-full h-full bg-blue-400 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-lg"></div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300">
              {service.title}
            </h3>
            <div className="text-xs font-medium text-blue-600 mt-1.5 uppercase tracking-wider flex items-center">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
              {service.category.charAt(0).toUpperCase() + service.category.slice(1)}
            </div>
          </div>
        </div>

        {/* Descripción mejorada para servicios */}
        <div className="mb-2 mt-2">
          <p className="text-gray-600 leading-relaxed border-l-2 border-blue-300 pl-3 py-1 bg-blue-50 bg-opacity-50 rounded-r-md">
            {service.description}
          </p>
        </div>
      </div>

      {/* Estadísticas de impacto para el servicio - Movido al final de la tarjeta */}
      {showStats && (
        <div className="px-4 pt-0 pb-2 relative z-10">
          <div className="bg-gradient-to-r from-blue-50 to-white p-3 rounded-lg border border-blue-100">
            <h4 className="text-xs font-semibold text-blue-800 mb-2 flex items-center">
              <BarChart className="h-3 w-3 mr-1" />
              Impacto en su negocio:
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {serviceStats.map((stat, index) => (
                <div key={index} className="text-center p-1.5 bg-white rounded-md shadow-sm">
                  <div className="flex justify-center mb-1">{stat.icon}</div>
                  <div className="text-xs font-medium text-gray-500">{stat.label}</div>
                  <div className="text-sm font-bold text-blue-700">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-100 relative z-10">
        <button
          onClick={handleNavigateToService}
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium group transition-all duration-300 w-full justify-between"
        >
          <div className="flex items-center">
            <span className="relative overflow-hidden">
              <span className="relative z-10">Ver detalles completos</span>
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
            </span>
          </div>
          <div className="bg-blue-100 rounded-full p-1 transform group-hover:translate-x-1 transition-all duration-300 group-hover:bg-blue-200">
            <ChevronRight className="h-4 w-4 text-blue-700" />
          </div>
        </button>
      </div>
    </motion.div>
  );
};

export default EnhancedServiceCard;
